import { useState, useEffect } from "react";
import { toast } from "sonner";
import { BellRing, Send, Smartphone } from "lucide-react";
import apiClient from "../../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import ResultDialog from "./ResultDialog";

const ROLE_LABELS = { admin: "Admin", project_manager: "Project Manager", office_manager: "Office Manager", foreman: "Foreman" };

const PushTab = () => {
  const [recipients, setRecipients] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/communications/push-recipients")
      .then((res) => { setRecipients(res.data.recipients || []); setConfigured(res.data.configured !== false); })
      .catch(() => toast.error("Failed to load recipients"))
      .finally(() => setLoading(false));
  }, []);

  const allSelected = recipients.length > 0 && selected.length === recipients.length;
  const toggleAll = () => setSelected(allSelected ? [] : recipients.map((r) => r.id));
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const canSend = title.trim() && body.trim() && selected.length > 0 && configured;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    try {
      const res = await apiClient.post("/communications/send-push", { user_ids: selected, title: title.trim(), body: body.trim() });
      setResult(res.data);
      if (res.data.sent_count > 0) toast.success(`Push sent to ${res.data.sent_count} recipient(s)`);
      if (res.data.failed_count > 0) toast.error(`${res.data.failed_count} failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send push");
    } finally { setSending(false); }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="push-tab-content">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BellRing className="w-5 h-5" /> Send Push Notification</CardTitle>
          <p className="text-sm text-muted-foreground">
            Sends an alert to each person's signed-in devices and saves it to their in-app bell.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!configured && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="push-not-configured">
              Push notifications are not configured on the server.
            </p>
          )}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="e.g., Rain delay tomorrow" data-testid="push-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={500} placeholder="Type your message..." data-testid="push-body-input" />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Recipients ({selected.length} selected)</Label>
              <Button variant="ghost" size="sm" onClick={toggleAll} disabled={recipients.length === 0} data-testid="push-select-all">
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </div>
            {recipients.length === 0 ? (
              <p className="text-center text-muted-foreground py-4" data-testid="push-no-recipients">
                No one has a signed-in device with notifications enabled yet
              </p>
            ) : (
              <div className="border rounded-lg max-h-[300px] overflow-y-auto divide-y">
                {recipients.map((r) => (
                  <label key={r.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer" data-testid={`push-recipient-${r.id}`}>
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`push-recipient-checkbox-${r.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{r.name || r.username}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Smartphone className="w-3 h-3" /> {r.device_count} device{r.device_count === 1 ? "" : "s"}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">{ROLE_LABELS[r.role] || r.role}</Badge>
                  </label>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleSend} disabled={sending || !canSend} className="w-full" data-testid="send-push-btn">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : `Send Push to ${selected.length} Recipient(s)`}
          </Button>
        </CardContent>
      </Card>

      {result && <ResultDialog result={result} type="Push" onClose={() => setResult(null)} />}
    </div>
  );
};

export default PushTab;
